// src/services/intake-form-service.js
// CRUD for intake form templates and responses.
// Template writes are practitioner-only (authenticated).
// Response reads are practitioner-only; form submission goes through Cloud Function.

import { db } from '../config/firebase-config';
import {
  collection, doc, getDocs, getDoc,
  addDoc, updateDoc, deleteDoc,
  query, where, orderBy, serverTimestamp,
} from 'firebase/firestore';

const FUNCTIONS_BASE = 'https://us-west1-divine-frameworks-6cb16.cloudfunctions.net';

// ─── Intake Form Templates ──────────────────────────────────────────────────

export const getIntakeForms = async (practitionerId) => {
  if (!db || !practitionerId) return [];
  try {
    const q = query(
      collection(db, 'practitioners', practitionerId, 'intakeForms'),
      orderBy('createdAt', 'desc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('getIntakeForms error:', err);
    return [];
  }
};

export const getIntakeFormById = async (practitionerId, formId) => {
  if (!db || !practitionerId || !formId) return null;
  try {
    const snap = await getDoc(doc(db, 'practitioners', practitionerId, 'intakeForms', formId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (err) {
    console.error('getIntakeFormById error:', err);
    return null;
  }
};

export const createIntakeForm = async (practitionerId, formData) => {
  if (!db || !practitionerId) throw new Error('Missing practitionerId');
  const ref = await addDoc(
    collection(db, 'practitioners', practitionerId, 'intakeForms'),
    {
      title: formData.title || '',
      description: formData.description || '',
      questions: formData.questions || [],
      assignedServices: formData.assignedServices || [],
      deadlineDays: formData.deadlineDays ?? 2,
      deadlineAction: formData.deadlineAction || 'notify',
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
  );
  return ref.id;
};

export const updateIntakeForm = async (practitionerId, formId, updates) => {
  if (!db || !practitionerId || !formId) throw new Error('Missing args');
  await updateDoc(
    doc(db, 'practitioners', practitionerId, 'intakeForms', formId),
    { ...updates, updatedAt: serverTimestamp() },
  );
};

export const deleteIntakeForm = async (practitionerId, formId) => {
  if (!db || !practitionerId || !formId) throw new Error('Missing args');
  await deleteDoc(doc(db, 'practitioners', practitionerId, 'intakeForms', formId));
};

// ─── Intake Responses ───────────────────────────────────────────────────────

export const getIntakeResponses = async (practitionerId, clientEmail) => {
  if (!db || !practitionerId || !clientEmail) return [];
  try {
    // Single-field filter only — no orderBy — to avoid requiring a composite index.
    // Sort client-side instead.
    const q = query(
      collection(db, 'practitioners', practitionerId, 'intakeResponses'),
      where('clientEmail', '==', clientEmail.toLowerCase().trim()),
    );
    const snap = await getDocs(q);
    const results = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    results.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.()?.getTime() || 0;
      const bTime = b.createdAt?.toDate?.()?.getTime() || 0;
      return bTime - aTime;
    });
    return results;
  } catch (err) {
    console.error('getIntakeResponses error:', err.code, err.message);
    return [];
  }
};

// ─── Cloud Function Calls ───────────────────────────────────────────────────

/**
 * Fetch intake form data for the client-facing form page.
 * Returns form questions, status, deadline, practitioner name, etc.
 */
export const fetchIntakeFormData = async (practitionerId, token) => {
  const resp = await fetch(
    `${FUNCTIONS_BASE}/getIntakeFormData?practitionerId=${encodeURIComponent(practitionerId)}&token=${encodeURIComponent(token)}`,
    { method: 'GET', headers: { 'Content-Type': 'application/json' } },
  );
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to load form');
  }
  return resp.json();
};

/**
 * Submit intake form answers (called from public IntakeFormPage).
 * Saves responses, marks status completed, creates practitioner notification.
 */
export const submitIntakeResponse = async (practitionerId, responseId, token, answers) => {
  const resp = await fetch(`${FUNCTIONS_BASE}/submitIntakeForm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ practitionerId, responseId, token, answers }),
  });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to submit form');
  }
  return resp.json();
};

/**
 * Resend intake form email (called from practitioner dashboard).
 */
export const resendIntakeEmail = async (practitionerId, responseId) => {
  const resp = await fetch(`${FUNCTIONS_BASE}/resendIntakeEmail`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ practitionerId, responseId }),
  });
  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to resend email');
  }
  return resp.json();
};
